import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Brain, Loader2, Globe, Sparkles, ImagePlus, X, MessageSquare } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import MarkdownRenderer from "./MarkdownRenderer";

export default function ChatWindow({ chat, messages, onSendMessage, isSending }) {
  const [input, setInput] = useState("");
  const [webSearch, setWebSearch] = useState(false);
  const [deepReasoning, setDeepReasoning] = useState(false);
  const [imageBase64, setImageBase64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const supportsVision = chat?.provider_type === "openai" || chat?.provider_type === "anthropic";

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const lastMsg = messages[messages.length - 1];
  const streamTrigger = lastMsg?._streaming ? lastMsg.content.length : 0;
  useEffect(() => { if (streamTrigger > 0) scrollToBottom(); }, [streamTrigger, scrollToBottom]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((!input.trim() && !imageBase64) || isSending) return;
    onSendMessage(input.trim(), { web_search: webSearch, deep_reasoning: deepReasoning, image_base64: imageBase64 });
    setInput("");
    setImageBase64(null);
    setImagePreview(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!supportsVision) return;
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setImageBase64(base64);
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeImage = () => {
    setImageBase64(null);
    setImagePreview(null);
  };

  if (!chat) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-[#080B10] border-r border-[#273142] px-6 py-12 text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-[#1C2433] border border-[#273142] mb-6">
          <img src="/logo.png" alt="Anymind" className="w-10 h-10 rounded-lg" />
        </div>
        <h3 className="text-base font-semibold text-white mb-2">No chat selected</h3>
        <p className="text-sm text-[#94A3B8] mb-8 max-w-xs" data-testid="empty-chat-state">
          Select a chat from the sidebar or create a new one to get started with your multi-LLM conversations.
        </p>
        <p className="text-[10px] text-[#475569] font-mono uppercase tracking-wider">ANYMIND // MULTI-LLM ROUTER</p>
      </div>
    );
  }

  return (
    <div data-testid="chat-window" className="flex-1 flex flex-col bg-[#080B10] border-r border-[#273142]">
      {/* Chat Header */}
      <div className="px-6 py-3 border-b border-[#273142] flex items-center gap-3">
        <h2 className="text-sm font-semibold tracking-tight truncate">{chat.title}</h2>
        <div className="flex items-center gap-2 ml-auto">
          {chat.provider_type && (
            <span className="bg-[#1C2433] text-[#94A3B8] border border-[#273142] font-mono text-[10px] px-2 py-1 uppercase">
              {chat.provider_type}
            </span>
          )}
          {chat.model_name && (
            <span className="bg-[#1C2433] text-[#94A3B8] border border-[#273142] font-mono text-[10px] px-2 py-1">
              {chat.model_name}
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1">
        <div className="min-h-full flex flex-col justify-end">
          {messages.length === 0 && (
            <div className="flex-1 flex items-center justify-center py-20 px-6">
              <div className="text-center max-w-sm">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#1C2433] border border-[#273142] mx-auto mb-4">
                  <MessageSquare size={20} className="text-[#E5FF00]" />
                </div>
                <p className="text-[#E5FF00] font-mono text-xs uppercase tracking-widest mb-2">Ready to start</p>
                <p className="text-[#94A3B8] text-sm mb-1">Begin typing to start your conversation</p>
                <p className="text-[#475569] text-xs font-mono">with your configured AI providers</p>
              </div>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              data-testid={`message-${msg.id}`}
              className={
                msg.role === "user"
                  ? "w-full bg-[#080B10] border-y border-[#273142]/50 px-6 py-4 flex gap-4 animate-fade-in"
                  : "w-full bg-[#111620] border-y border-[#273142]/50 border-l-2 border-l-[#E5FF00] px-6 py-4 flex gap-4 animate-fade-in"
              }
            >
              <div className="flex-shrink-0 w-6 h-6 border border-[#273142] flex items-center justify-center">
                {msg.role === "user" ? (
                  <span className="text-[10px] font-mono text-[#94A3B8]">U</span>
                ) : (
                  <span className="text-[10px] font-mono text-[#E5FF00]">AI</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#475569]">
                    {msg.role}
                  </span>
                  {msg.memory_context && (
                    <span className="bg-[#E5FF00]/10 text-[#E5FF00] border border-[#E5FF00]/30 font-mono text-[10px] px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1">
                      <Brain size={10} /> MEMORY
                    </span>
                  )}
                  {msg.web_search_context && (
                    <span className="bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30 font-mono text-[10px] px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1">
                      <Globe size={10} /> WEB
                    </span>
                  )}
                  {msg.deep_reasoning && (
                    <span className="bg-[#A78BFA]/10 text-[#A78BFA] border border-[#A78BFA]/30 font-mono text-[10px] px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1">
                      <Sparkles size={10} /> DEEP
                    </span>
                  )}
                  {msg._webSearchUsed && (
                    <span className="bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30 font-mono text-[10px] px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1">
                      <Globe size={10} /> WEB
                    </span>
                  )}
                  {msg._deepReasoning && (
                    <span className="bg-[#A78BFA]/10 text-[#A78BFA] border border-[#A78BFA]/30 font-mono text-[10px] px-1.5 py-0.5 uppercase tracking-widest flex items-center gap-1">
                      <Sparkles size={10} /> DEEP
                    </span>
                  )}
                </div>
                {/* Image in message */}
                {msg.image_base64 && (
                  <div className="mb-2">
                    <img
                      src={`data:image/png;base64,${msg.image_base64}`}
                      alt="Uploaded"
                      className="max-w-[300px] max-h-[200px] border border-[#273142] object-contain"
                    />
                  </div>
                )}
                {msg._imagePreview && (
                  <div className="mb-2">
                    <img src={msg._imagePreview} alt="Uploaded" className="max-w-[300px] max-h-[200px] border border-[#273142] object-contain" />
                  </div>
                )}
                <div className="text-sm leading-relaxed break-words prose-invert max-w-none">
                  {msg.role === "assistant" ? (
                    <MarkdownRenderer content={msg.content} />
                  ) : (
                    <span className="whitespace-pre-wrap">{msg.content}</span>
                  )}
                  {msg._streaming && (
                    <span className="inline-block w-2 h-4 bg-[#E5FF00] animate-blink ml-0.5 align-middle" />
                  )}
                </div>
              </div>
            </div>
          ))}
          {isSending && !messages.some((m) => m._streaming) && (
            <div className="w-full bg-[#111620] border-y border-[#273142]/50 border-l-2 border-l-[#E5FF00] p-6 flex gap-4 animate-fade-in">
              <div className="flex-shrink-0 w-6 h-6 border border-[#273142] flex items-center justify-center">
                <span className="text-[10px] font-mono text-[#E5FF00]">AI</span>
              </div>
              <div className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-[#94A3B8]" />
                <span className="font-mono text-xs text-[#94A3B8]">
                  {webSearch ? "Searching web..." : deepReasoning ? "Deep thinking..." : "Connecting..."}
                </span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-[#273142]">
        {/* Toolbar */}
        <div className="px-4 py-2 border-b border-[#273142]/50 flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="toggle-web-search"
                  type="button"
                  onClick={() => setWebSearch(!webSearch)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-all border rounded-full ${
                    webSearch
                      ? "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/40"
                      : "bg-transparent text-[#475569] border-[#273142] hover:border-[#475569] hover:text-[#94A3B8]"
                  }`}
                >
                  <Globe size={12} /> Search
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Web search via Tavily</p></TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="toggle-deep-reasoning"
                  type="button"
                  onClick={() => setDeepReasoning(!deepReasoning)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-all border rounded-full ${
                    deepReasoning
                      ? "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/40"
                      : "bg-transparent text-[#475569] border-[#273142] hover:border-[#475569] hover:text-[#94A3B8]"
                  }`}
                >
                  <Sparkles size={12} /> Deep
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Deep reasoning mode (chain-of-thought + reasoning model)</p></TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="toggle-image-upload"
                  type="button"
                  onClick={() => {
                    if (!supportsVision) return;
                    fileInputRef.current?.click();
                  }}
                  disabled={!supportsVision}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-all border rounded-full ${
                    imageBase64
                      ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/40"
                      : supportsVision
                        ? "bg-transparent text-[#475569] border-[#273142] hover:border-[#475569] hover:text-[#94A3B8]"
                        : "bg-transparent text-[#273142] border-[#1C2433] cursor-not-allowed"
                  }`}
                >
                  <ImagePlus size={12} /> Image
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  {supportsVision ? "Attach image (PNG, JPG, WEBP)" : "Vision not supported by this provider"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageSelect}
            className="hidden"
            data-testid="image-file-input"
          />

          <div className="ml-auto flex items-center gap-1.5">
            <Brain size={10} className="text-[#E5FF00]" />
            <span className="font-mono text-[10px] text-[#E5FF00] uppercase tracking-widest">Memory Active</span>
          </div>
        </div>

        {/* Image preview */}
        {imagePreview && (
          <div className="px-4 py-2 border-b border-[#273142]/50 flex items-center gap-3 bg-[#111620]">
            <img src={imagePreview} alt="Preview" className="h-16 max-w-[120px] object-contain border border-[#273142]" />
            <div className="flex-1">
              <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Image attached</p>
            </div>
            <button
              data-testid="remove-image"
              onClick={removeImage}
              className="text-[#475569] hover:text-[#FF3366] transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex items-end gap-2 p-3">
          <Textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={imageBase64 && !input ? "Describe this image... (or just send)" : "Type your message..."}
            disabled={isSending}
            rows={1}
            className="flex-1 bg-[#111620] border border-[#273142] text-white p-3 focus:outline-none resize-none font-sans text-sm rounded-xl min-h-[48px] max-h-[200px] focus-visible:ring-1 focus-visible:ring-[#E5FF00] focus-visible:ring-offset-0"
          />
          <button
            type="submit"
            data-testid="chat-submit-button"
            disabled={isSending || (!input.trim() && !imageBase64)}
            className="bg-[#E5FF00] text-[#080B10] w-11 h-11 rounded-xl hover:bg-[#D4EC00] transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
